<script setup lang="ts">
import type {
  EventResponse,
  CommitmentResponse,
  RegistrationTypeResponse,
} from '@@/types/api';
import { formatCurrency } from '~/composables/format-currency';

const props = defineProps<{
  modelValue: boolean;
  event: EventResponse;
  commitment: CommitmentResponse;
  isSubmitting: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  submit: [targetTypeId: string];
}>();

const selectedTypeId = ref('');

const currentType = computed<RegistrationTypeResponse | undefined>(() =>
  props.event.registration_types?.find(
    (rt) => rt.id === props.commitment.registration_type_id,
  ),
);

const otherTypes = computed<RegistrationTypeResponse[]>(
  () =>
    props.event.registration_types?.filter(
      (rt) => rt.id !== props.commitment.registration_type_id,
    ) ?? [],
);

const selectedType = computed<RegistrationTypeResponse | undefined>(() =>
  otherTypes.value.find((rt) => rt.id === selectedTypeId.value),
);

const priceDelta = computed(() => {
  if (!currentType.value || !selectedType.value) return null;
  return (
    selectedType.value.pricing.commit.total -
    currentType.value.pricing.commit.total
  );
});

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      // Pre-select first available type
      const available = otherTypes.value.filter((rt) => !rt.is_sold_out);
      selectedTypeId.value = available[0]?.id ?? '';
    } else {
      selectedTypeId.value = '';
    }
  },
);

function close() {
  emit('update:modelValue', false);
}
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="520"
    :persistent="isSubmitting"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="text-h6">Switch registration type</v-card-title>
      <v-card-text>
        <!-- Current type -->
        <div v-if="currentType" class="mb-4">
          <div class="text-caption text-medium-emphasis mb-1">Current type</div>
          <div class="d-flex align-center ga-2">
            <span class="text-body-2 font-weight-medium">
              {{ currentType.name }}
            </span>
            <span class="text-body-2 text-medium-emphasis">
              {{ formatCurrency(currentType.pricing.commit.total) }}
            </span>
          </div>
        </div>

        <v-divider class="mb-4" />

        <!-- Type selector -->
        <div class="text-subtitle-2 mb-2">Switch to</div>
        <v-radio-group v-model="selectedTypeId" class="mb-4" hide-details>
          <v-radio
            v-for="rt in otherTypes"
            :key="rt.id"
            :value="rt.id"
            :disabled="rt.is_sold_out"
            class="mb-1"
          >
            <template #label>
              <div class="d-flex align-center ga-2 w-100">
                <div>
                  <span class="text-body-2 font-weight-medium">
                    {{ rt.name }}
                  </span>
                  <span class="text-body-2 text-medium-emphasis ml-2">
                    {{ formatCurrency(rt.pricing.commit.total) }}
                  </span>
                </div>
                <v-chip
                  v-if="rt.is_sold_out"
                  color="error"
                  size="x-small"
                  label
                >
                  Sold out
                </v-chip>
                <span
                  v-else-if="rt.registrations_remaining != null"
                  class="text-caption text-medium-emphasis"
                >
                  {{ rt.registrations_remaining }} left
                </span>
              </div>
            </template>
          </v-radio>
        </v-radio-group>

        <!-- Price delta -->
        <v-alert
          v-if="priceDelta !== null && priceDelta !== 0"
          :type="priceDelta > 0 ? 'warning' : 'info'"
          density="compact"
          variant="tonal"
          class="mb-4"
        >
          <template v-if="priceDelta > 0">
            Price increases by {{ formatCurrency(priceDelta) }}
          </template>
          <template v-else>
            Price decreases by {{ formatCurrency(Math.abs(priceDelta)) }}
          </template>
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="isSubmitting" @click="close">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :disabled="!selectedTypeId || isSubmitting"
          :loading="isSubmitting"
          @click="emit('submit', selectedTypeId)"
        >
          Switch type
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
